import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Sparkle, PaperPlaneTilt, Plus, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./AiChatDialog.css";

// Splits assistant content into paragraphs (double newline, fallback to single)
export function splitParagraphs(content) {
  let parts = (content || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) {
    parts = (content || "")
      .split(/\n/)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return parts;
}

// **bold** → <strong>, built with safe React elements
export function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) => {
    if (chunk.startsWith("**") && chunk.endsWith("**")) {
      return <strong key={i}>{chunk.slice(2, -2)}</strong>;
    }
    return <span key={i}>{chunk}</span>;
  });
}

function renderParagraph(paragraph) {
  return paragraph.split("\n").map((line, i) => {
    const headingMatch = line.match(/^#{2,3}\s*(.*)$/);
    if (headingMatch) {
      return (
        <div key={i} className="ai-chat-line ai-chat-line-heading">
          <strong>{renderInline(headingMatch[1])}</strong>
        </div>
      );
    }
    const listMatch = line.match(/^[-•]\s+(.*)$/);
    if (listMatch) {
      return (
        <div key={i} className="ai-chat-line ai-chat-line-list">
          <span className="ai-chat-bullet">•</span>
          <span>{renderInline(listMatch[1])}</span>
        </div>
      );
    }
    return (
      <div key={i} className="ai-chat-line">
        {renderInline(line)}
      </div>
    );
  });
}

// Paragraph text with markdown markers stripped, for insertion into the report
function toPlainText(paragraph) {
  return paragraph
    .split("\n")
    .map((line) => line.replace(/^#{1,6}\s*/, "").replace(/^[-•]\s+/, ""))
    .join("\n")
    .replace(/\*\*/g, "");
}

function formatTimestamp(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return timestamp;
  return date.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Jerusalem",
  });
}

function AiChatDialog({
  open,
  onClose,
  messages,
  isLoading,
  error,
  onSendMessage,
  onInsertText,
  onRetry,
}) {
  const [inputText, setInputText] = useState("");
  const [selectionBtn, setSelectionBtn] = useState(null);
  const bodyRef = useRef(null);
  const textareaRef = useRef(null);

  const messageCount = messages?.length || 0;

  useEffect(() => {
    if (!open || !bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [open, messageCount, isLoading, error]);

  // Hide the floating insert button when the selection collapses
  useEffect(() => {
    if (!open) return;
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) setSelectionBtn(null);
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, [open]);

  const handleSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setSelectionBtn(null);
      return;
    }
    const toElement = (node) =>
      node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    const anchorEl = toElement(sel.anchorNode);
    const focusEl = toElement(sel.focusNode);
    if (
      !anchorEl?.closest?.(".ai-chat-msg-assistant") ||
      !focusEl?.closest?.(".ai-chat-msg-assistant")
    ) {
      setSelectionBtn(null);
      return;
    }
    const text = sel.toString().trim();
    if (!text) {
      setSelectionBtn(null);
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    const top = Math.max(8, rect.top - 40);
    const left = Math.min(
      Math.max(8, rect.left + rect.width / 2 - 50),
      window.innerWidth - 110
    );
    setSelectionBtn({ top, left, text });
  }, []);

  const handleInsertSelection = () => {
    if (!selectionBtn) return;
    onInsertText(selectionBtn.text);
    setSelectionBtn(null);
    window.getSelection()?.removeAllRanges();
  };

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || isLoading) return;
    onSendMessage(text);
    setInputText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="ai-chat-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ai-chat-modal">
        <div className="ai-chat-header">
          <div className="ai-chat-header-title">
            <Sparkle size={22} weight="duotone" />
            <h3>התייעצות עם AI</h3>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div
          className="ai-chat-body"
          ref={bodyRef}
          onMouseUp={handleSelection}
          onTouchEnd={handleSelection}
        >
          {messageCount === 0 && !isLoading && !error && (
            <div className="ai-chat-empty">
              <Sparkle size={24} weight="duotone" />
              <p className="ai-chat-empty-hint">
                אפשר לשאול את ה-AI שאלות על הדוח. ה-AI מכיר את מה שמולא בטופס,
                את סוג הקליניקה וגיל ומגדר המטופל — ללא פרטים מזהים.
              </p>
              <p className="ai-chat-empty-warning">
                <WarningCircle size={14} weight="fill" />
                שימו לב: ה-AI עונה בביטחון מלא גם כשהוא טועה — אין להסתמך עליו
                בנושאים רגישים.
              </p>
            </div>
          )}

          {messages?.map((msg, idx) => (
            <div
              key={idx}
              className={`ai-chat-msg ${
                msg.role === "user" ? "ai-chat-msg-user" : "ai-chat-msg-assistant"
              }`}
            >
              {msg.role === "assistant" ? (
                splitParagraphs(msg.content).map((paragraph, pIdx) => (
                  <div key={pIdx} className="ai-chat-paragraph">
                    <div className="ai-chat-paragraph-text">
                      {renderParagraph(paragraph)}
                    </div>
                    <button
                      type="button"
                      className="ai-chat-insert-btn"
                      title="הוסף לדוח"
                      aria-label="הוסף לדוח"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onInsertText(toPlainText(paragraph))}
                    >
                      <Plus size={14} weight="bold" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="ai-chat-msg-text">{msg.content}</div>
              )}
              {msg.timestamp && (
                <span className="ai-chat-msg-time">
                  {formatTimestamp(msg.timestamp)}
                </span>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="ai-chat-msg ai-chat-msg-assistant ai-chat-typing">
              <span className="ai-chat-typing-dot" />
              <span className="ai-chat-typing-dot" />
              <span className="ai-chat-typing-dot" />
            </div>
          )}

          {error && (
            <div className="ai-chat-msg ai-chat-msg-error">
              <div className="ai-chat-error-text">
                <WarningCircle size={16} weight="fill" />
                שגיאה בקבלת תשובה מה-AI
              </div>
              <button
                type="button"
                className="ai-chat-retry-btn"
                onClick={onRetry}
              >
                <ArrowClockwise size={13} weight="bold" />
                נסה שוב
              </button>
            </div>
          )}
        </div>

        <div className="ai-chat-footer">
          <textarea
            ref={textareaRef}
            className="ai-chat-input"
            placeholder="כתבו שאלה ל-AI..."
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            rows={1}
          />
          <button
            type="button"
            className="ai-chat-send-btn"
            onClick={handleSend}
            disabled={isLoading || !inputText.trim()}
            aria-label="שליחה"
          >
            <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </div>
      </div>

      {selectionBtn && (
        <button
          type="button"
          className="ai-chat-selection-btn"
          style={{ top: selectionBtn.top, left: selectionBtn.left }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleInsertSelection}
        >
          <Plus size={13} weight="bold" />
          הוסף לדוח
        </button>
      )}
    </div>,
    document.body
  );
}

export default AiChatDialog;
